/**
 * Panel de la pestaña "Pelear": cielo, isla flotante con el nombre del
 * bioma/capítulo actual, y el botón grande para empezar la run.
 */

import Phaser from 'phaser';
import type { ChapterDef } from '../../core/types';
import { biomeLabel } from '../../core/biomePalette';
import { PALETTE } from './palette';
import { UiButton } from './uiKit';

export class PlayPanel extends Phaser.GameObjects.Container {
  constructor(
    scene: Phaser.Scene,
    width: number,
    height: number,
    chapter: ChapterDef,
    onFight: () => void
  ) {
    super(scene, 0, 0);

    // cielo con degradé simple
    const sky = scene.add.graphics();
    sky.fillGradientStyle(PALETTE.sky1, PALETTE.sky1, PALETTE.sky2, PALETTE.sky2, 1);
    sky.fillRect(0, 0, width, height);

    // un par de nubes
    const clouds = scene.add.graphics();
    clouds.fillStyle(0xffffff, 0.85);
    drawCloud(clouds, width * 0.22, height * 0.16, 30);
    drawCloud(clouds, width * 0.72, height * 0.26, 22);
    drawCloud(clouds, width * 0.5, height * 0.09, 18);

    // isla flotante
    const islandCx = width / 2;
    const islandCy = height * 0.44;
    const island = scene.add.graphics();
    drawIsland(island, islandCx, islandCy, Math.min(width * 0.62, 300));

    const chapterName = scene.add
      .text(islandCx, islandCy - 18, chapter.name, {
        fontFamily: 'monospace',
        fontSize: '20px',
        color: '#241c3d',
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5);

    const biomeName = scene.add
      .text(islandCx, islandCy + 8, biomeLabel(chapter.biome).toUpperCase(), {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#3f2f1a',
        align: 'center',
      })
      .setOrigin(0.5);

    const waveCount = scene.add
      .text(islandCx, islandCy + 26, `${chapter.waves.length} oleadas`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#5c4630',
      })
      .setOrigin(0.5);

    const fightButton = new UiButton(
      scene,
      islandCx,
      height * 0.82,
      Math.max(220, width * 0.6),
      64,
      'PELEAR',
      onFight,
      {
        fill: PALETTE.accent,
        fillHover: PALETTE.accentSoft,
        border: PALETTE.accentDark,
        textColor: '#241c3d',
        fontSize: '22px',
        radius: 18,
      }
    );

    this.add([sky, clouds, island, chapterName, biomeName, waveCount, fightButton]);
    this.setSize(width, height);
    scene.add.existing(this);
  }
}

function drawCloud(g: Phaser.GameObjects.Graphics, x: number, y: number, size: number): void {
  g.fillCircle(x, y, size * 0.5);
  g.fillCircle(x - size * 0.55, y + size * 0.12, size * 0.35);
  g.fillCircle(x + size * 0.55, y + size * 0.12, size * 0.35);
}

function drawIsland(g: Phaser.GameObjects.Graphics, cx: number, cy: number, w: number): void {
  const grassH = w * 0.32;
  const rockH = w * 0.4;

  // roca inferior (trapecio irregular con "gotas" de tierra colgando)
  g.fillStyle(PALETTE.islandRockDark, 1);
  g.fillTriangle(cx - w * 0.32, cy + grassH * 0.3, cx + w * 0.32, cy + grassH * 0.3, cx, cy + grassH * 0.3 + rockH);
  g.fillStyle(PALETTE.islandRock, 1);
  g.fillEllipse(cx, cy + grassH * 0.3, w * 0.64, grassH * 0.55);

  // césped superior
  g.fillStyle(PALETTE.islandGrassDark, 1);
  g.fillEllipse(cx, cy, w, grassH * 1.1);
  g.fillStyle(PALETTE.islandGrass, 1);
  g.fillEllipse(cx, cy - grassH * 0.12, w * 0.96, grassH);

  // un par de arbustos decorativos
  g.fillStyle(PALETTE.islandGrassDark, 1);
  g.fillCircle(cx - w * 0.3, cy - grassH * 0.05, w * 0.05);
  g.fillCircle(cx + w * 0.34, cy - grassH * 0.02, w * 0.045);
}
